import {
  presentEnvironmentRecovery,
  type EnvironmentConnectionPresentation,
} from "@afar-research/client-runtime/state/presentation";
import type { EnvironmentId } from "@afar-research/contracts";

import type { PrimaryEnvironmentDiscovery } from "../connection/primaryEnvironmentDiscovery";

/**
 * Whether the workspace shell can open yet. Until an Environment delivers its first snapshot
 * the shell has nothing true to show, so every surface stands in with a placeholder. The Runtime
 * Web Client also gates a settled account with no connected Environment.
 */
export type WorkspaceShellEntry =
  | { readonly kind: "ready" }
  | { readonly kind: "synchronizing" }
  | { readonly kind: "no-environment" }
  /** `environmentId` is null when the out-of-reach Environment is the primary one this client
   * could not find, so it has no catalog entry to name. */
  | { readonly kind: "unavailable"; readonly environmentId: EnvironmentId | null };

const READY = { kind: "ready" } as const;
const SYNCHRONIZING = { kind: "synchronizing" } as const;
const NO_ENVIRONMENT = { kind: "no-environment" } as const;
const PRIMARY_UNREACHABLE = { kind: "unavailable", environmentId: null } as const;

/**
 * Decide the shell's entry from the Environments the client knows.
 *
 * Ordinarily only the first snapshot gates entry: a snapshot outlives a reconnect, and the
 * surfaces that answer for a stale Environment already do so per thread. The Runtime Web Client
 * instead shows connection recovery when all known Environments are unavailable, or a setup gate
 * when its settled catalog is empty. Other clients open on their empty state once discovery ends;
 * a primary Environment that is not answering is not "none".
 *
 * An Environment counts as out of reach only once it has failed or is waiting to retry.
 * One that has not tried yet is about to, and calling it unreachable would flash a failure
 * on every load. One whose endpoint is still starting, such as a Cloud Workspace whose run
 * is coming up, is still loading: it keeps the skeleton until it connects or is refused.
 */
export function resolveWorkspaceShellEntry(input: {
  readonly environmentsSettled: boolean;
  readonly runtimeWeb?: boolean;
  readonly primaryDiscovery: PrimaryEnvironmentDiscovery;
  readonly hasSnapshot: boolean;
  readonly environments: ReadonlyArray<{
    readonly environmentId: EnvironmentId;
    readonly enabled: boolean;
    readonly connection: Pick<
      EnvironmentConnectionPresentation,
      "phase" | "error" | "willRetryAutomatically" | "endpointStarting"
    >;
  }>;
}): WorkspaceShellEntry {
  const environments = input.runtimeWeb
    ? input.environments.filter((environment) => environment.enabled)
    : input.environments;
  if (input.runtimeWeb && input.environmentsSettled && environments.length === 0)
    return NO_ENVIRONMENT;
  if (input.runtimeWeb && input.environmentsSettled && environments.length > 0) {
    const allUnavailable = environments.every(
      (environment) =>
        environment.connection.phase === "unavailable" && !environment.connection.endpointStarting,
    );
    if (allUnavailable) {
      const unreachable = environments.find(
        (environment) =>
          environment.connection.error !== null || environment.connection.willRetryAutomatically,
      );
      if (unreachable !== undefined)
        return { kind: "unavailable", environmentId: unreachable.environmentId };
    }
  }
  if (input.hasSnapshot) return READY;
  if (input.primaryDiscovery === "searching") return SYNCHRONIZING;
  if (
    environments.some(
      (environment) =>
        environment.connection.phase !== "unavailable" || environment.connection.endpointStarting,
    )
  ) {
    return SYNCHRONIZING;
  }
  const unreachable = environments.find(
    (environment) =>
      environment.connection.error !== null || environment.connection.willRetryAutomatically,
  );
  if (unreachable !== undefined) {
    return { kind: "unavailable", environmentId: unreachable.environmentId };
  }
  if (input.primaryDiscovery === "unreachable") return PRIMARY_UNREACHABLE;
  if (environments.length === 0) return input.environmentsSettled ? READY : SYNCHRONIZING;
  return SYNCHRONIZING;
}

export function sameWorkspaceShellEntry(
  left: WorkspaceShellEntry,
  right: WorkspaceShellEntry,
): boolean {
  return left.kind === "unavailable" && right.kind === "unavailable"
    ? left.environmentId === right.environmentId
    : left.kind === right.kind;
}

/**
 * What the recovery view says about the Environment holding the shell. The titlebar names the
 * Environment and this says the rest once. A connection that comes back on its own is waited
 * for, not reported as broken: a network that comes back resumes the connection, and calling
 * that "out of reach" would announce a failure that is not happening.
 */
export function presentWorkspaceShellUnavailable(input: {
  readonly label: string;
  readonly connection: EnvironmentConnectionPresentation;
}): { readonly waiting: boolean; readonly title: string; readonly detail: string } {
  const reason = input.connection.error ?? `${input.label} is not connected.`;
  return presentEnvironmentRecovery(input.connection).reconnectsOnItsOwn
    ? {
        waiting: true,
        title: `Waiting for ${input.label}`,
        detail: `${reason} Its projects and threads appear once it connects.`,
      }
    : {
        waiting: false,
        title: "Your Environment is out of reach",
        detail: `${reason} Its projects and threads cannot be shown until it reconnects, which it will not do on its own.`,
      };
}
